'use client';

/**
 * Pure data derivation for V2 address fields: yup schema contribution and
 * default-value population. This hook owns data only; no RHF, no DOM, no
 * rendering. See packages/components/docs/design-decisions/0001-address-fields-decomposition.md
 * for why this is separated from FormAddressFields.
 */

import {
  ADDRESS_PREFIX_SEPARATOR,
  SupportedCountryCode,
  TAddress,
  stringUtils,
} from '@verifime/utils';
import { useMemo } from 'react';
import { getFieldsRulesV1ForAddressOnly } from '../../Form';
import { TAddEntityAddressesV2 } from './useAddEntityV2';
import { COUNTRY_ADDRESS_FIELDS_AND_RULES } from './utils';

/**
 * Default-value source for the address fields, populated from a retrieved
 * customer payload. Two disjoint shapes match the entity groupings:
 *
 *   - Personal entities (Individual, Trust) populate `address`.
 *   - Business entities (Company, Sole Trader, Partnership) populate
 *     `registeredOffice` and/or `principalPlaceOfBusiness`.
 *
 * The union prevents a single source from carrying both `address` and the
 * business-only keys at once; that combination never occurs for a real
 * customer and would be ambiguous for the renderer.
 */
export type TDefaultAddressSource =
  | {
      address: TAddress;
      registeredOffice?: never;
      principalPlaceOfBusiness?: never;
    }
  | {
      address?: never;
      registeredOffice?: TAddress;
      principalPlaceOfBusiness?: TAddress;
    };

type UseAddressFieldsDataArgs = {
  addresses: TAddEntityAddressesV2;
  selectedCountryCode: SupportedCountryCode;
  defaultAddressSource: TDefaultAddressSource | null;
};

export function useAddressFieldsData({
  addresses,
  selectedCountryCode,
  defaultAddressSource,
}: UseAddressFieldsDataArgs): {
  addressesSchemaContribution: Record<string, unknown>;
  defaultAddressFieldsValues: Record<string, string | undefined>;
} {
  const addressesSchemaContribution = useMemo(() => {
    return (addresses ?? []).reduce((tally, address) => {
      return {
        ...tally,
        ...getFieldsRulesV1ForAddressOnly(
          COUNTRY_ADDRESS_FIELDS_AND_RULES[selectedCountryCode][address.fieldsAndRulesName],
        ),
      };
    }, {});
  }, [addresses, selectedCountryCode]);

  const defaultAddressFieldsValues = useMemo<Record<string, string | undefined>>(() => {
    return (addresses ?? []).reduce<Record<string, string | undefined>>((tallyAddresses, address) => {
      const addressFieldName = address.fieldsPrefix.split(
        ADDRESS_PREFIX_SEPARATOR,
      )[0] as keyof TDefaultAddressSource;
      const addressData = defaultAddressSource?.[addressFieldName] as TAddress | undefined;
      if (addressData && addressData.countryCode === selectedCountryCode) {
        const defaultValues = Object.entries(addressData).reduce<Record<string, string | undefined>>(
          (tally, [k, v]) => ({
            ...tally,
            [stringUtils.generateCamelCaseString(address.fieldsPrefix, k)]: v as string | undefined,
          }),
          {},
        );
        return { ...tallyAddresses, ...defaultValues };
      }
      return tallyAddresses;
    }, {});
  }, [defaultAddressSource, selectedCountryCode, addresses]);

  return {
    addressesSchemaContribution,
    defaultAddressFieldsValues,
  };
}

export default useAddressFieldsData;
